// ============================================================================
// quickAccessHubData.ts — БАТЧ-ДАННЫЕ виджета «Быстрый доступ» (SSoT-источники
// ядра, без выдумок):
//   • БЕЙДЖИ (число записей/непрочитанных) — СЧЁТЧИКИ ПАПОК объекта
//     (FolderService.FolderCounts: total по отборам + unread из objects_reads).
//     Папки (системные/общие) есть только у задач/заявок, поэтому у остальных
//     объектов бейджа НЕТ (источника нет — не выдумываем).
//   • ПРАВО «Создать» — существующий источник прав объекта
//     (GetCatalog/GetDocument.rights.canCreate; у типов задач/заявок права в
//     списке не отдаются — как в AutoListForm: создание доступно, проверит
//     сервер). `canCreate === false` — действие скрывается.
// ОДНА батч-загрузка (Promise.all по уникальным объектам), а НЕ запрос на
// карточку. Ошибка отдельного объекта не роняет остальные (лог + пропуск).
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';

const log = createLogger('QuickAccessHub');

/** Источник бейджа/права — тип объекта + код. */
export interface HubRef {
  objectType: string;
  objectName: string;
}

/** Бейдж-счётчик строки (папка «Все» объекта). */
export interface HubBadge {
  total: number;
  unread: number;
}

export interface HubEntry {
  /** Бейдж: есть источник счётчика — есть бейдж; нет — undefined. */
  badge?: HubBadge;
  /** Право «Создать»: undefined — источник прав не дал ответа. */
  canCreate?: boolean;
}

export function hubRefKey(r: HubRef): string {
  return `${r.objectType}.${r.objectName}`;
}

/** Бейджи — только у объектов с папками ядра (задачи/заявки). */
const BADGE_TYPES = new Set(['task', 'order']);

function uniqRefs(refs: HubRef[]): HubRef[] {
  const seen = new Set<string>();
  const out: HubRef[] = [];
  for (const r of refs) {
    if (!r.objectType || !r.objectName) continue;
    const key = hubRefKey(r);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/** Счётчики папок → бейдж по папке «Все» (код all). */
async function loadBadge(objectType: string, objectName: string): Promise<HubBadge | undefined> {
  if (!BADGE_TYPES.has(objectType)) return undefined;
  const r = await getSDK().folders.folderCounts({ entityType: objectType, entityCode: objectName });
  const all = r.items.find((c) => String(c.code) === 'all');
  if (!all) return undefined;
  const total = Number(all.total) || 0;
  const unread = Number(all.unread) || 0;
  if (total <= 0 && unread <= 0) return undefined;
  return { total, unread };
}

/** Право «Создать» — из существующего источника прав объекта. */
async function loadCanCreate(objectType: string, objectName: string): Promise<boolean | undefined> {
  if (objectType === 'catalog') {
    const c = await getSDK().metadata.getCatalog({ id: objectName });
    return c.rights ? c.rights.canCreate : undefined;
  }
  if (objectType === 'document') {
    const d = await getSDK().metadata.getDocument({ id: objectName });
    return d.rights ? d.rights.canCreate : undefined;
  }
  // Типы задач/заявок: права в списке типов не отдаются — как в AutoListForm
  // (при отсутствии прав создание доступно; окончательно проверит сервер).
  if (objectType === 'task' || objectType === 'order') return true;
  return undefined;
}

/** Батч-загрузка данных виджета по уникальным объектам (одним проходом). */
export async function loadHubEntries(refs: HubRef[]): Promise<Record<string, HubEntry>> {
  const list = uniqRefs(refs);
  const out: Record<string, HubEntry> = {};
  await Promise.all(list.map(async (r) => {
    const entry: HubEntry = {};
    try {
      entry.badge = await loadBadge(r.objectType, r.objectName);
    } catch (e) {
      log.warn('badge failed: ' + ((e as Error)?.message || String(e)), { objectType: r.objectType, objectName: r.objectName });
    }
    try {
      entry.canCreate = await loadCanCreate(r.objectType, r.objectName);
    } catch (e) {
      log.warn('rights failed: ' + ((e as Error)?.message || String(e)), { objectType: r.objectType, objectName: r.objectName });
    }
    out[hubRefKey(r)] = entry;
  }));
  return out;
}
